import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Users, UserPlus, Phone, PhoneCall, PhoneOff, CalendarClock, AlarmClock, CalendarDays, CalendarCheck, FileText, Handshake, Trophy, XCircle, Layers, TrendingUp, IndianRupee, Target, Percent } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, inr, inrShort, fmtT, VERTICAL_HEX } from "@/lib/api";
import { FilterBar, defaultFilters, toParams } from "@/components/FilterBar";
import { Kpi, PageHeader, Section, Empty, StageBadge } from "@/components/common";
import { RevenueFunnel } from "@/components/RevenueFunnel";

const TodayPanel = ({ userId }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    const p = userId ? { user_id: userId } : {};
    Promise.all([api.get("/tasks", { params: { bucket: "today", ...p } }), api.get("/tasks", { params: { bucket: "overdue", ...p } }),
      api.get("/meetings", { params: { bucket: "today", ...p } }), api.get("/leads", { params: { stage: "New Lead", ...p } })])
      .then(([t, o, m, l]) => setD({ tasks: t.data, overdue: o.data, meetings: m.data, toCall: l.data.filter((x) => !x.last_contacted).slice(0, 8) }));
  }, [userId]);
  if (!d) return null;
  const Col = ({ title, items, render, testid, link }) => (
    <div className="min-w-0" data-testid={testid}>
      <div className="mb-2 flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{title} <span className="num text-primary">{items.length}</span></p>
        <Link to={link} className="text-[11px] font-semibold text-primary hover:underline">View all</Link>
      </div>
      <div className="space-y-1.5">{items.length ? items.slice(0, 5).map(render) : <p className="text-xs text-muted-foreground">Nothing pending</p>}</div>
    </div>
  );
  const Row = ({ to, a, b, tone = "" }) => (
    <Link to={to} className={`block truncate rounded-md border px-3 py-2 text-sm transition-colors hover:border-primary ${tone}`}>
      <span className="font-semibold">{a}</span> <span className="text-xs text-muted-foreground">{b}</span>
    </Link>
  );
  return (
    <Section title="What needs to be done today" className="mb-6">
      <div className="grid gap-6 p-4 md:grid-cols-2 xl:grid-cols-4">
        <Col title="Follow-ups due today" items={d.tasks} testid="today-followups" link="/tasks" render={(t) => <Row key={t.id} to={t.lead_id ? `/leads/${t.lead_id}` : "/tasks"} a={t.client || t.title} b={`${fmtT(t.due_at)} · ${t.type}`} />} />
        <Col title="Overdue follow-ups" items={d.overdue} testid="today-overdue" link="/tasks" render={(t) => <Row key={t.id} to={t.lead_id ? `/leads/${t.lead_id}` : "/tasks"} a={t.client || t.title} b={t.type} tone="border-rose-200 bg-rose-50/50" />} />
        <Col title="Today's meetings" items={d.meetings} testid="today-meetings" link="/meetings" render={(m) => <Row key={m.id} to={`/leads/${m.lead_id}`} a={m.client} b={`${fmtT(m.scheduled_at)} · ${m.status}`} />} />
        <Col title="New leads to call" items={d.toCall} testid="today-to-call" link="/leads" render={(l) => <Row key={l.id} to={`/leads/${l.id}`} a={l.company} b={l.phone} />} />
      </div>
    </Section>
  );
};

const PerfTable = ({ rows }) => (
  <Section title="Salesperson performance" className="mb-6">
    <div className="overflow-x-auto">
      <table className="crm-table" data-testid="performance-table">
        <thead><tr>{["Salesperson", "Calls", "Connected", "Meetings", "Follow-ups", "Proposals", "Won", "Revenue", "Target", "Achievement %"].map((h) => <th key={h}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.user_id} data-testid={`performance-row-${r.user_id}`}>
              <td className="font-semibold">{i === 0 && r.month_revenue > 0 && <Trophy className="mr-1 inline h-3.5 w-3.5 text-amber-500" />}{r.name}</td>
              <td className="num">{r.calls}</td><td className="num">{r.connected}</td><td className="num">{r.meetings}</td>
              <td className="num">{r.followups}</td><td className="num">{r.proposals}</td><td className="num">{r.won}</td>
              <td className="num font-semibold">{inr(r.month_revenue)}</td><td className="num">{inr(r.target)}</td>
              <td>
                <div className="flex items-center gap-2">
                  <div className="h-1.5 w-20 rounded-full bg-slate-100"><div className={`h-1.5 rounded-full ${r.achievement >= 100 ? "bg-emerald-500" : r.achievement >= 60 ? "bg-amber-500" : "bg-rose-500"}`} style={{ width: `${Math.min(100, r.achievement)}%` }} /></div>
                  <span className="num text-xs font-semibold">{r.achievement}%</span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!rows.length && <Empty />}
    </div>
  </Section>
);

export default function Dashboard() {
  const { isAdmin, user } = useAuth();
  const [sp] = useSearchParams();
  const [f, setF] = useState(defaultFilters("month", { user_id: sp.get("user") || "" }));
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard", { params: toParams(f) }).then((r) => setD(r.data)); }, [f]);
  const k = d?.kpis;
  return (
    <div>
      <PageHeader title={isAdmin ? "Sales Command Center" : `Good day, ${user.name.split(" ")[0]}`} subtitle={isAdmin ? "Complete team performance across BMS, Intl, Ind and CRM" : "Your leads, calls, pipeline and target at a glance"} />
      <FilterBar value={f} onChange={setF} />
      {!isAdmin && <TodayPanel />}
      {isAdmin && f.user_id && <TodayPanel userId={f.user_id} />}
      {k && (
        <>
          <Section title={`Revenue: Target → Pipeline → Forecast → Won → Gap (${k.month})`} className="mb-6"
            action={<span className="num text-sm font-bold text-primary" data-testid="target-achievement">{k.achievement}% achieved</span>}>
            <RevenueFunnel data={d.funnel} />
          </Section>
          <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6" data-testid="kpi-grid">
            <Kpi testid="kpi-total-leads" label="Total leads" value={k.total_leads} icon={Users} />
            <Kpi testid="kpi-new-leads" label="New leads" value={k.new_leads} icon={UserPlus} tone="brand" />
            <Kpi testid="kpi-calls" label={isAdmin ? "Total calls" : "Calls made"} value={k.calls} icon={Phone} tone="blue" />
            <Kpi testid="kpi-connected" label="Connected calls" value={k.connected} icon={PhoneCall} tone="green" />
            <Kpi testid="kpi-no-answer" label="No answers" value={k.no_answer} icon={PhoneOff} tone="amber" />
            <Kpi testid="kpi-followups-due" label="Follow-ups due today" value={k.followups_due_today} icon={CalendarClock} tone="blue" />
            <Kpi testid="kpi-overdue" label="Overdue follow-ups" value={k.followups_overdue} icon={AlarmClock} tone="red" />
            <Kpi testid="kpi-meetings-scheduled" label="Meetings scheduled" value={k.meetings_scheduled} icon={CalendarDays} tone="violet" />
            <Kpi testid="kpi-meetings-completed" label="Meetings completed" value={k.meetings_completed} icon={CalendarCheck} tone="green" />
            <Kpi testid="kpi-proposals" label="Proposals sent" value={k.proposals} icon={FileText} tone="amber" />
            <Kpi testid="kpi-negotiations" label="Negotiations" value={k.negotiations} icon={Handshake} tone="violet" />
            <Kpi testid="kpi-won" label="Deals won" value={k.won} icon={Trophy} tone="green" />
            <Kpi testid="kpi-lost" label="Deals lost" value={k.lost} icon={XCircle} tone="red" />
            <Kpi testid="kpi-pipeline" label={isAdmin ? "Total pipeline value" : "Pipeline value"} value={inrShort(k.pipeline_value)} sub={`${k.open_deals} open deals`} icon={Layers} tone="violet" />
            <Kpi testid="kpi-expected" label="Expected revenue" value={inrShort(k.expected_revenue)} sub="Weighted by probability" icon={TrendingUp} tone="amber" />
            <Kpi testid="kpi-revenue" label="Revenue generated" value={inrShort(k.revenue)} sub="In selected period" icon={IndianRupee} tone="green" />
            <Kpi testid="kpi-target" label="Monthly target" value={inrShort(k.target)} sub={`Won this month ${inrShort(k.month_revenue)}`} icon={Target} tone="blue" />
            <Kpi testid="kpi-achievement" label="Target achievement" value={`${k.achievement}%`} sub={`Gap ${inrShort(k.gap)}`} icon={Percent} tone={k.achievement >= 100 ? "green" : "brand"} />
          </div>
          {isAdmin && <PerfTable rows={d.performance} />}
          <div className="grid gap-6 xl:grid-cols-3">
            {isAdmin && (
              <Section title="Vertical performance">
                <div className="space-y-4 p-4" data-testid="vertical-performance">
                  {Object.entries(d.verticals).map(([v, x]) => (
                    <div key={v} data-testid={`dashboard-vertical-${v}`}>
                      <div className="flex justify-between text-sm"><span className="font-bold" style={{ color: VERTICAL_HEX[v] }}>{v}</span><span className="num text-xs text-muted-foreground">Won {inrShort(x.month_revenue)} / Target {inrShort(x.target)} · <b className="text-primary">{x.achievement}%</b></span></div>
                      <div className="mt-1 h-2 rounded-full bg-slate-100"><div className="h-2 rounded-full" style={{ background: VERTICAL_HEX[v], width: `${Math.min(100, x.target ? x.achievement : 0)}%` }} /></div>
                      <p className="num mt-1 text-[11px] text-muted-foreground">Pipeline {inrShort(x.pipeline)} · Gap {inrShort(x.gap)}</p>
                    </div>
                  ))}
                </div>
              </Section>
            )}
            <Section title="Needs immediate attention" className={isAdmin ? "xl:col-span-2" : "xl:col-span-3"}>
              <div className="divide-y" data-testid="attention-list">
                {d.attention.length === 0 && <Empty text="All deals are on track" />}
                {d.attention.map((a) => (
                  <Link key={a.id} to={`/leads/${a.id}`} className="flex flex-wrap items-center gap-3 px-4 py-2.5 transition-colors hover:bg-slate-50">
                    <span className="min-w-40 flex-1 font-semibold">{a.company}</span>
                    <StageBadge s={a.stage} />
                    {isAdmin && <span className="text-xs text-muted-foreground">{a.assigned_name}</span>}
                    <span className="flex flex-wrap gap-1">{a.reasons.map((r) => <span key={r} className="rounded bg-rose-50 px-1.5 py-0.5 text-[11px] font-semibold text-rose-700">{r}</span>)}</span>
                    <span className="num w-20 text-right text-sm">{inrShort(a.deal_value)}</span>
                  </Link>
                ))}
              </div>
            </Section>
          </div>
        </>
      )}
    </div>
  );
}
